import { useState } from "react";
import { Button, ErrorMessage, Flex, ModalV2, Text, Textarea } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";

export type ReturnForEditsModalProps = {
  // Original Format's mockup copy calls this "Request edits" rather than "Return for edits" —
  // undefined keeps today's DdqRequest/BackfillDdq wording unchanged.
  variant?: "originalFormat";
  makerName: string | undefined;
  errorMessage: string | null;
  isSubmitting: boolean;
  onHide: () => void;
  onConfirm: (comment: string) => void;
};

export const ReturnForEditsModal = ({
  variant,
  makerName,
  errorMessage,
  isSubmitting,
  onHide,
  onConfirm,
}: ReturnForEditsModalProps) => {
  const [comment, setComment] = useState("");
  const title = variant === "originalFormat" ? "Request edits?" : "Return for edits?";
  const confirmLabel = variant === "originalFormat" ? "Request edits" : "Return for edits";

  return (
    <ModalV2
      show
      onHide={onHide}
      title={title}
      size="small"
      data-testid="ddq-return-for-edits-modal"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <Text variant="secondary-md">
            This will reopen the DDQ for edits and return it to {makerName ?? "the assignee"}.
          </Text>
          <Textarea
            isFullWidth
            label={`Leave a comment for ${makerName ?? "the assignee"}`}
            isRequired
            placeholder="Explain what needs to change"
            value={comment}
            onSetValue={setComment}
            data-testid="ddq-return-for-edits-comment"
          />
          {errorMessage && (
            <ErrorMessage data-testid="ddq-return-for-edits-error">{errorMessage}</ErrorMessage>
          )}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" gap={SPACING.SMALL} justifyContent="flex-end">
          <Button variant="neutral" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            disabled={!comment.trim() || isSubmitting}
            onClick={() => onConfirm(comment)}
            data-testid="ddq-return-for-edits-confirm"
          >
            {confirmLabel}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
